import { NavigationDirection, TabNavigationOptions } from '@/types/navigation';

/**
 * NavigationUtils provides utility functions for navigation operations
 */
export class NavigationUtils {
  
  /**
   * Determine navigation direction based on tab positions
   */
  static getNavigationDirection(fromTabId: string, toTabId: string): NavigationDirection {
    const tabOrder = ['discover', 'conversations', 'settings'];
    const fromIndex = tabOrder.indexOf(fromTabId);
    const toIndex = tabOrder.indexOf(toTabId);
    
    if (fromIndex === -1 || toIndex === -1) {
      return 'forward';
    }
    
    return toIndex > fromIndex ? 'forward' : 'backward';
  }

  /**
   * Create navigation options with smart defaults
   */
  static createNavigationOptions(
    fromTabId?: string,
    toTabId?: string,
    overrides?: Partial<TabNavigationOptions>
  ): TabNavigationOptions {
    const defaultOptions: TabNavigationOptions = {
      preventHistory: false,
      direction: 'forward',
    };

    if (fromTabId && toTabId) {
      defaultOptions.direction = this.getNavigationDirection(fromTabId, toTabId);
    }

    return { ...defaultOptions, ...overrides };
  }

  /**
   * Check if navigation should be animated
   */
  static shouldAnimateTransition(
    fromTabId: string,
    toTabId: string,
    options?: TabNavigationOptions
  ): boolean {
    // Don't animate if explicitly disabled
    if (options?.preventHistory) return false;
    
    // Don't animate for replace operations
    if (options?.direction === 'replace') return false;
    
    // Animate for adjacent tabs
    const tabOrder = ['discover', 'conversations', 'settings'];
    const fromIndex = tabOrder.indexOf(fromTabId);
    const toIndex = tabOrder.indexOf(toTabId);
    
    return Math.abs(fromIndex - toIndex) === 1;
  }

  /**
   * Get animation class name for tab transition
   */
  static getTransitionClassName(
    direction: NavigationDirection,
    isEntering: boolean
  ): string {
    const baseClass = 'transition-transform duration-300 ease-in-out';
    
    if (direction === 'forward') {
      return `${baseClass} ${isEntering ? 'translate-x-0' : '-translate-x-full'}`;
    } else if (direction === 'backward') {
      return `${baseClass} ${isEntering ? 'translate-x-0' : 'translate-x-full'}`;
    } else {
      return `${baseClass} ${isEntering ? 'opacity-100' : 'opacity-0'}`;
    }
  }

  /**
   * Validate navigation parameters
   */
  static validateNavigation(
    tabId: string,
    options?: TabNavigationOptions
  ): { isValid: boolean; error?: string } {
    // Check if tab ID is valid
    const validTabIds = ['discover', 'conversations', 'settings'];
    if (!validTabIds.includes(tabId)) {
      return {
        isValid: false,
        error: `Invalid tab ID: ${tabId}. Valid IDs are: ${validTabIds.join(', ')}`
      };
    }

    // Validate direction
    if (options?.direction && !['forward', 'backward', 'replace'].includes(options.direction)) {
      return {
        isValid: false,
        error: `Invalid direction: ${options.direction}`
      };
    }

    return { isValid: true };
  }

  /**
   * Get safe tab ID (fallback to default if invalid)
   */
  static getSafeTabId(tabId: string): string {
    const validTabIds = ['discover', 'conversations', 'settings'];
    return validTabIds.includes(tabId) ? tabId : 'discover';
  }

  /**
   * Format navigation history for debugging
   */
  static formatHistory(history: string[]): string {
    return history.length > 0 ? history.join(' → ') : 'No history';
  }

  /**
   * Check if user can navigate back
   */
  static canGoBack(history: string[]): boolean {
    return history.length > 1;
  }

  /**
   * Get previous tab from history
   */
  static getPreviousTab(history: string[]): string | null {
    return history.length > 1 ? history[history.length - 2] : null;
  }

  /**
   * Cleanup navigation history (remove duplicates, limit size)
   */
  static cleanupHistory(
    history: string[], 
    maxLength: number = 10
  ): string[] {
    // Remove consecutive duplicates
    const cleaned = history.reduce<string[]>((acc, current) => {
      if (acc.length === 0 || acc[acc.length - 1] !== current) {
        acc.push(current);
      }
      return acc;
    }, []);

    // Limit history length
    return cleaned.length > maxLength ? cleaned.slice(-maxLength) : cleaned;
  }

  /**
   * Parse URL path to determine active tab
   */
  static parsePathToTab(pathname: string): string {
    if (pathname === '/' || pathname.startsWith('/discover')) {
      return 'discover';
    }
    
    if (pathname.startsWith('/conversations')) {
      return 'conversations';
    }
    
    if (pathname.startsWith('/settings')) {
      return 'settings';
    }
    
    // Default fallback
    return 'discover';
  }

  /**
   * Check if current path should show navigation
   */
  static shouldShowNavigation(pathname: string): boolean {
    // Don't show on these paths
    const hiddenPaths = ['/setup', '/login', '/register'];
    const hiddenPatterns = [/^\/conversation\/[^/]+$/];
    
    return !hiddenPaths.includes(pathname) && 
           !hiddenPatterns.some(pattern => pattern.test(pathname));
  }
}

export default NavigationUtils;